/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * The icon set lives in the kit (GM Skyhawk artwork where it has a match, Material Symbols Rounded
 * for the rest) so every app draws from one sheet. This module keeps the ~90 existing
 * `@/components/Icon` imports and their names working: the kit answers to the app's old names
 * (`chevd`, `warn`, `vehicle`, `trash`, ...) as aliases. The two app names older kit builds have no
 * glyph for are resolved here, preferring the kit's own glyph once it ships one.
 *
 * Kit icons are filled geometry (`fill="currentColor"`), not stroked paths, and carry a
 * `data-icon` attribute naming the glyph - query that in tests rather than path data.
 */
import {
  Icon as KitIcon,
  iconNames,
  type IconName as KitIconName,
  type IconProps as KitIconProps,
} from '@vaas/react-ui-kit';

const KIT_GLYPHS: ReadonlySet<string> = new Set(iconNames);

/**
 * App names with no glyph in older kit builds: the kit glyph that means it, and the glyph the
 * approved GMDS design drew in its place.
 */
const APP_ICONS = {
  /** The assistant: a robot where the kit has one, else the GMDS AI sparkle. */
  bot: { glyph: 'smart_toy', fallback: 'auto_awesome' },
  /** Off-Line repair: a vehicle pulled off the line into a repair bay. */
  lift: { glyph: 'construction', fallback: 'construction' },
} as const satisfies Record<string, { glyph: string; fallback: KitIconName }>;

/** Any kit icon name, or one of the app's own names above. */
export type IconName = KitIconName | keyof typeof APP_ICONS;

export interface IconProps extends Omit<KitIconProps, 'name'> {
  name: IconName;
}

function kitName(name: IconName): KitIconName {
  if (!Object.hasOwn(APP_ICONS, name)) return name as KitIconName;
  const { glyph, fallback } = APP_ICONS[name as keyof typeof APP_ICONS];
  return KIT_GLYPHS.has(glyph) ? (glyph as KitIconName) : fallback;
}

/** Decorative by default (`aria-hidden`); pass `title` to give it an accessible name. */
export function Icon({ name, ...props }: IconProps) {
  return <KitIcon name={kitName(name)} {...props} />;
}
